import React, { useEffect, useState } from 'react';
import { Grid, Typography, Paper, Card, CardContent, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@material-ui/core';
import { Button, TextField, NumberField, MdSpace } from 'm2m.components';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import {
  EmissaoStatus,
  InstrumentoFinanceiroApiResponse,
  RegistroStatus,
  type HistoricoLancamentoPU,
} from 'common/genericTypes';
import { addCpfCnpjMask } from 'utils/mask';
import InstrumentoAutocomplete from 'components/operacoes/instrumentoAutocomplete';
import moment from 'moment';
import { addAlert } from 'store/modules/snackBar';
import 'moment/locale/pt-br';
import { useStyles } from './styles';

const ERRO_LANCAMENTO_DUPLICADO = 'Já existe um lançamento de PU para a data de encerramento da distribuição. Não é possível realizar outro lançamento nesta data.';

const LancamentoPU: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const getInstrumentosApi = useSelector((state: RootState) => state.instrumentoApi.getInstrumentos);
  const getInstrumentoApi = useSelector((state: RootState) => state.instrumentoApi.getInstrumentoDetalhes);
  const postLancamentoPU = useSelector((state: RootState) => state.instrumentoApi.postLancamentoPU);
  const usuario = useSelector((state: RootState) => state.auth.usuario);
  const [instrumentos, setInstrumentos] = useState<Array<InstrumentoFinanceiroApiResponse>>([]);
  const [instrumento, setInstrumento] = useState<InstrumentoFinanceiroApiResponse | null>(null);
  const [pu, setPu] = useState<number>(0);
  const [puOriginal, setPuOriginal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [historico, setHistorico] = useState<HistoricoLancamentoPU[]>([]);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const verificarLancamentoNaDataEncerramento = (): boolean => {
    if (!instrumento?.dataEncerramentoDistribuicao) return false;
    
    const dataEncerramento = moment(instrumento.dataEncerramentoDistribuicao).format('YYYY-MM-DD');
    
    const lancamentoNaDataEncerramento = historico.some(item => {
      const dataLancamento = moment(item.data).format('YYYY-MM-DD');
      return dataLancamento === dataEncerramento;
    });
    
    return lancamentoNaDataEncerramento;
  };

  const verificarDataEncerramentoJaPassou = (): boolean => {
    if (!instrumento?.dataEncerramentoDistribuicao) return false;
    
    const hoje = moment().format('YYYY-MM-DD');
    const dataEncerramento = moment(instrumento.dataEncerramentoDistribuicao).format('YYYY-MM-DD');
    
    return moment(hoje).isAfter(dataEncerramento);
  };

  const isDataEncerramentoDistribuicao = (): boolean => {
    if (!instrumento?.dataEncerramentoDistribuicao) return false;
    
    const hoje = moment().format('YYYY-MM-DD');
    const dataEncerramento = moment(instrumento.dataEncerramentoDistribuicao).format('YYYY-MM-DD');
    
    return hoje === dataEncerramento;
  };

  const handleConfirmSubmit = async () => {
    setOpenDialog(false);
    await executarLancamentoPU();
  };

  const executarLancamentoPU = async () => {
    if (!instrumento?.instrumentoFinanceiroGuid || loading) return;
    
    setLoading(true);
    try {
      await postLancamentoPU(
          dispatch,
          instrumento.instrumentoFinanceiroGuid,
          pu
      );
      dispatch(addAlert({ severity: 'success', message: 'PU lançado com sucesso!' }));
      setPuOriginal(pu);
      await buscarInstrumentoDetalhes(instrumento.instrumentoFinanceiroGuid);
    } catch (error) {
      console.error('Erro ao lançar PU:', error);
      dispatch(addAlert({ severity: 'error', message: 'Erro ao lançar o PU. Tente novamente.' }));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    setErrorMessage('');
    
    if (pu < 0) {
      setErrorMessage('O valor do PU não pode ser negativo.');
      dispatch(addAlert({ severity: 'error', message: 'O valor do PU não pode ser negativo.' }));
      return;
    }

    if (verificarDataEncerramentoJaPassou()) {
      setErrorMessage('Não é possível lançar PU após a data de encerramento da distribuição.');
      dispatch(addAlert({ severity: 'error', message: 'Não é possível lançar PU após a data de encerramento da distribuição.' }));
      return;
    }
    
    if (verificarLancamentoNaDataEncerramento()) {
      setErrorMessage(ERRO_LANCAMENTO_DUPLICADO);
      dispatch(addAlert({ severity: 'error', message: 'Já existe um lançamento de PU para a data de encerramento da distribuição.' }));
      return;
    }
    
    if (isDataEncerramentoDistribuicao()) {
      setOpenDialog(true);
      return;
    }
    
    executarLancamentoPU();
  };

  useEffect(() => {
    const fetchInstrumentos = async () => {
      const result = await getInstrumentosApi(
        dispatch,
        {
          emissaoStatus: [EmissaoStatus.NaoEmitido, EmissaoStatus.ParcialmenteEmitido],
          registroStatus: [RegistroStatus.Aprovado],
          coordenadorId: usuario?.participante.id,
        },
        true
      );
      setInstrumentos(result);
    };
    fetchInstrumentos();
  }, [dispatch, getInstrumentosApi, usuario?.participante.id]);
  
  useEffect(() => {
    if (verificarLancamentoNaDataEncerramento()) {
      setErrorMessage(ERRO_LANCAMENTO_DUPLICADO);
    } else {
      setErrorMessage('');
    }
  }, [instrumento, historico]);

  const buscarInstrumentoDetalhes = async (instrumentoGuid: string) => {
    try {
      const instrumentoDetalhes = await getInstrumentoApi(
        dispatch,
        instrumentoGuid,
        true
      );

      setInstrumento(instrumentoDetalhes);
      const valorPu = instrumentoDetalhes?.criteriosRemuneracao?.valorNominalUnitarioEmissao || 0;
      setPu(valorPu);
      setPuOriginal(valorPu);
      setHistorico(instrumentoDetalhes.criteriosRemuneracao?.historicoLancamentoPUs || []);
      return instrumentoDetalhes;
    } catch (error) {
      console.error('Erro ao buscar detalhes do instrumento:', error);
      return null;
    }
  };
  
  const fetchInstrumentos = async (value: any) => {
    if (!value?.instrumentoFinanceiroGuid) return;
    await buscarInstrumentoDetalhes(value.instrumentoFinanceiroGuid);
  };

  return (
    <div style={{ marginRight: 40, marginBottom: 40 }}>
      <Typography variant="h2">Lançamento de PU</Typography>
      <MdSpace />

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper style={{ padding: 24 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <InstrumentoAutocomplete
                  instrumentos={instrumentos}
                  onChange={async (value: any) => {
                    await fetchInstrumentos(value);
                  }}
                  instrumento={instrumento}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Razão social do Emissor"
                  value={instrumento?.emissor?.razaoSocial || ''}
                  fullWidth
                  disabled
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="CNPJ do Emissor"
                  fullWidth
                  value={addCpfCnpjMask(instrumento?.emissor?.cnpj || '')}
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  disabled
                  label="Data de Emissão"
                  fullWidth
                  value={
                    instrumento?.criteriosRemuneracao?.dataEmissao
                      ? moment(instrumento.criteriosRemuneracao.dataEmissao).format('DD/MM/YYYY')
                      : ''
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  disabled
                  label="Data Limite Distribuição"
                  fullWidth
                  value={
                    instrumento?.dataEncerramentoDistribuicao
                      ? moment(instrumento.dataEncerramentoDistribuicao).format('DD/MM/YYYY')
                      : ''
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <NumberField
                  label="Valor Emissão"
                  value={instrumento?.criteriosRemuneracao?.volumeEmissao || 0}
                  onChange={(_) => _}
                  numberType="eightDecimals"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <NumberField
                  label="PU"
                  value={pu}
                  onChange={(value: number) => setPu(value)}
                  numberType="eightDecimals"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {errorMessage && (
          <Typography color="error" style={{ marginBottom: 16 }}>
            {errorMessage}
          </Typography>
        )}

        <Grid item xs={12} md={9}>
          {historico.length > 0 && (
            <div className={classes.historico}>
              <Typography variant="h6" className={classes.historicoTitle}>Histórico de Lançamentos</Typography>
              <Grid container spacing={2}>
                {[...historico]
                  .sort((a, b) => moment(b.data).valueOf() - moment(a.data).valueOf())
                  .map((item, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Card className={classes.card}>
                        <CardContent className={classes.cardContent}>
                          <Typography className={classes.cardTitle}>
                            {moment(item.data).format('DD/MM/YYYY HH:mm')}
                          </Typography>
                          <Typography>
                            Valor: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.valor)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
              </Grid>
            </div>
          )}
        </Grid>
        
        <Grid item xs={12} md={3} style={{ textAlign: 'right', marginTop: 24 }}>
          <Button 
            variant="contained" 
            color="primary" 
            disabled={!instrumento || loading || (pu === puOriginal && historico.length > 0)}
            onClick={handleSubmit}
          >
            {loading ? 'Processando...' : 'Confirmar'}
          </Button>
        </Grid>
      </Grid>
      
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title" className={classes.dialogTitle}>Confirmação de Lançamento</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <DialogContentText id="alert-dialog-description">
            Após aprovação sua operação será automaticamente liquidada sem alteração do PU.
          </DialogContentText>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button 
            onClick={() => setOpenDialog(false)} 
            variant="outlined" 
            className={classes.cancelButton}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmSubmit} 
            variant="contained" 
            color="primary" 
            className={classes.confirmButton} 
            autoFocus
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default LancamentoPU;
